import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, HTTPError, patch, post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type {
  CrmIntegrationSettings,
  CrmIntegrationSettingsItem,
  IntegrationCrmType,
  IntegrationSettings,
} from "common/types";

async function removeIntegrationRequest(
  workspaceId: string,
  integrationKey: keyof IntegrationSettings,
): Promise<string> {
  return (await del(`workspaces/${workspaceId}/integrations/${integrationKey}`))
    .install_url;
}

async function updateIntegrationRequest(
  workspaceId: string,
  integrationKey: keyof CrmIntegrationSettings,
  updates: CrmIntegrationSettingsItem,
) {
  await patch(
    `workspaces/${workspaceId}/integrations/${integrationKey}`,
    updates,
  );
}

async function connectIntegrationRequest(
  workspaceId: string,
  type: IntegrationCrmType,
  api_key: string,
): Promise<string> {
  return (
    await post(`workspaces/${workspaceId}/integrations/${type}`, { api_key })
  ).workspace_name;
}

export default function useIntegrationMutations() {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const queryClient = useQueryClient();

  const { mutateAsync: updateIntegration, isPending: isUpdatingIntegration } =
    useMutation({
      mutationFn: ({
        updates,
        integrationKey,
      }: {
        updates: CrmIntegrationSettingsItem;
        integrationKey: keyof CrmIntegrationSettings;
      }) => updateIntegrationRequest(workspaceId, integrationKey, updates),
      onSuccess: (_, { updates, integrationKey }) => {
        queryClient.setQueryData<IntegrationSettings>(
          getQueryKeys(workspaceId).integrationSettingsKey,
          (prevSettings) => ({
            ...prevSettings,
            [integrationKey]: updates,
          }),
        );
      },
      onError: () => toast.error("Failed to update integration"),
    });

  const { mutateAsync: removeIntegration, isPending: isRemovingIntegration } =
    useMutation({
      mutationFn: (integrationKey: keyof IntegrationSettings) =>
        removeIntegrationRequest(workspaceId, integrationKey),
      onSuccess: (installUrl, integrationKey) => {
        queryClient.setQueryData<IntegrationSettings>(
          getQueryKeys(workspaceId).integrationSettingsKey,
          (prevSettings) => ({
            ...prevSettings,
            [integrationKey]:
              integrationKey === "hubspot" || integrationKey === "pipedrive"
                ? { install_url: installUrl }
                : null,
          }),
        );

        toast.success("Integration removed");
      },
      onError: () => toast.error("Failed to remove integration"),
    });

  const {
    mutateAsync: connectIntegration,
    isPending: isConnectingIntegration,
  } = useMutation({
    mutationFn: ({
      type,
      apiKey,
    }: {
      type: IntegrationCrmType;
      apiKey: string;
    }) => connectIntegrationRequest(workspaceId, type, apiKey),
    onSuccess: (workspaceName, { type, apiKey }) => {
      queryClient.setQueryData<IntegrationSettings>(
        getQueryKeys(workspaceId).integrationSettingsKey,
        (prevSettings) => ({
          ...prevSettings,
          ...(type === "instantly"
            ? {
                instantly: {
                  workspace_name: workspaceName,
                },
              }
            : {
                smartlead: {
                  api_key: apiKey,
                },
              }),
        }),
      );

      toast.success(`Connected with ${type}`);
    },
    onError: (err) =>
      toast.error(
        err instanceof HTTPError && err.code === 401 && err.serverMessage
          ? err.serverMessage
          : "Failed to connect integration",
      ),
  });

  return {
    updateIntegration,
    isUpdatingIntegration,
    removeIntegration,
    isRemovingIntegration,
    connectIntegration,
    isConnectingIntegration,
  };
}
